import { useEffect, useMemo, useRef, useState } from 'react';
import type { MediaItem } from '../../types';
import { useApi } from '../api-context';

/**
 * Holds a window of decoded previews around the cursor and disposes the rest.
 *
 * Object URLs pin their blob in memory until revoked, so a long culling session
 * that forgets to revoke will climb until the tab dies. Every URL created here
 * is revoked exactly once, when it leaves the window or the component unmounts.
 *
 * The window is derived as a list of ids, and the loading effect keys off those
 * ids rather than off the array they came from. That distinction is the whole
 * point: Review rebuilds its item array on every Review Assist progress event,
 * and a background sharpness pass fires one of those every eight frames. Keyed
 * off the array, the effect tore down and restarted between every decode, so on
 * the first visit to a large shoot — the one visit where that pass is running —
 * no preview ever finished loading.
 */
export function usePreviewWindow(items: MediaItem[], index: number, radius = 3) {
  const api = useApi();
  const [urls, setUrls] = useState<Map<number, string>>(new Map());
  const held = useRef(new Map<number, string>());

  // Nearest first, so the frame being looked at is decoded before the read-ahead.
  const window = useMemo(() => {
    const near: number[] = [];
    for (let offset = -radius + 1; offset <= radius; offset++) {
      const item = items[index + offset];
      if (item) near.push(item.id);
    }
    return near.sort(
      (a, b) =>
        Math.abs(items.findIndex((i) => i.id === a) - index) -
        Math.abs(items.findIndex((i) => i.id === b) - index),
    );
  }, [items, index, radius]);

  const key = window.join(',');

  // Read by async work that started under an older window, so a decode that
  // finishes late can ask whether its frame is still wanted rather than being
  // thrown away for having been slow.
  const wanted = useRef<Set<number>>(new Set());
  wanted.current = new Set(window);

  const lookup = useRef(items);
  lookup.current = items;

  // Set false once the screen is left, so a decode that lands after unmount
  // releases its blob instead of adding it to a map nothing will ever revoke.
  const alive = useRef(true);

  useEffect(() => {
    // Release anything that has fallen outside the window.
    for (const [id, url] of held.current) {
      if (!wanted.current.has(id)) {
        URL.revokeObjectURL(url);
        held.current.delete(id);
      }
    }

    void (async () => {
      for (const id of window) {
        if (held.current.has(id)) continue;

        const item = lookup.current.find((i) => i.id === id);
        if (!item) continue;

        const url = await api.previewUrl(item);
        if (!url) continue;

        // Judged against the window as it is *now*, not as it was when this
        // decode started. Anything still on screen is kept even if a newer run
        // has taken over; anything scrolled past is released here and now.
        if (!alive.current || !wanted.current.has(id) || held.current.has(id)) {
          URL.revokeObjectURL(url);
          continue;
        }

        held.current.set(id, url);
        setUrls(new Map(held.current));
      }
    })();

    setUrls(new Map(held.current));
    // `key` stands in for `window`: same ids, same work.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, key]);

  // Unmount: nothing survives the screen being left.
  useEffect(() => {
    const map = held.current;
    const flag = alive;
    flag.current = true;
    return () => {
      flag.current = false;
      for (const url of map.values()) URL.revokeObjectURL(url);
      map.clear();
    };
  }, []);

  return urls;
}

/** One thumbnail, generated on demand and revoked when the tile unmounts. */
export function useThumbnail(item: MediaItem | null): string | null {
  const api = useApi();
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!item) return;
    let cancelled = false;
    let created: string | null = null;

    void (async () => {
      const next = await api.thumbnailUrl(item);
      if (cancelled) {
        if (next) URL.revokeObjectURL(next);
        return;
      }
      created = next;
      setUrl(next);
    })();

    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
      setUrl(null);
    };
  }, [api, item?.id, item?.thumbKey]);

  return url;
}
